import { useNavigate } from 'react-router';
import { getDocumentationUrl } from '@gonfalon/docs/documentationUrl';
import { enableModalConfirmationAutofocus } from '@gonfalon/dogfood-flags/dogfood-flags';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { toSecurity } from '@gonfalon/navigator/navigators';
import { Button, Dialog, Heading, IconButton, LinkButton, Modal, ModalOverlay } from '@launchpad-ui/components';

import { Stack } from '../../../components/Stack';
import { Text } from '../../../components/Text';

export type AiAccessMode = 'self-serve-form' | 'order-form-required' | 'disabled';

type Props = {
  product: string;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  mode: AiAccessMode;
};

export const AiAccessModal: React.FC<Props> = ({ isOpen, onOpenChange, product, mode }) => {
  const navigate = useNavigate();

  const orderFormRequired = mode === 'order-form-required';

  return (
    <ModalOverlay isOpen={isOpen} onOpenChange={onOpenChange}>
      <Modal>
        <Dialog>
          {({ close }) => (
            <>
              <div slot="header">
                <Heading slot="title">
                  {orderFormRequired ? (
                    <Message id="observability.aiAccessDialog.requestAccessTitle" />
                  ) : (
                    <Message id="observability.aiAccessDialog.enableTitle" />
                  )}
                </Heading>
                <IconButton
                  aria-label={msg('observability.aiAccessDialog.closeLabel')}
                  variant="minimal"
                  icon="cancel"
                  size="small"
                  onPress={close}
                />
              </div>
              <div slot="body">
                <Stack py="8" style={{ minWidth: 300, maxWidth: 500 }}>
                  <Text size="small" color="secondaryContentText">
                    {orderFormRequired ? (
                      <Message id="observability.aiAccessDialog.requestAccessBody" />
                    ) : (
                      <Message id="observability.aiAccessDialog.enableBody" values={{ product }} />
                    )}
                  </Text>
                </Stack>
              </div>
              <div slot="footer">
                <Stack direction="row" gap="8" justifyContent="flex-end">
                  {orderFormRequired ? (
                    <>
                      <Button variant="default" onPress={close}>
                        {msg('observability.aiAccessDialog.close')}
                      </Button>
                      <LinkButton
                        variant="primary"
                        href={getDocumentationUrl('home/getting-started/vega')}
                        target="_blank"
                        rel="noopener noreferrer"
                        autoFocus={enableModalConfirmationAutofocus()}
                      >
                        {msg('observability.aiAccessDialog.vegaEligibility')}
                      </LinkButton>
                    </>
                  ) : (
                    <>
                      <Button variant="default" onPress={close}>
                        {msg('observability.aiAccessDialog.cancel')}
                      </Button>
                      <Button
                        variant="primary"
                        autoFocus={enableModalConfirmationAutofocus()}
                        onPress={() => {
                          close();
                          void navigate(toSecurity({ hash: 'enable-genai' }));
                        }}
                      >
                        {msg('observability.aiAccessDialog.goToSettings')}
                      </Button>
                    </>
                  )}
                </Stack>
              </div>
            </>
          )}
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
};
